import { memo, useMemo, type FC } from 'react'
import type { GridApi, GridCellCtx, GridColumn, GridEditorProps } from './types'
import TextEditor from './editors/TextEditor'
import NumberEditor from './editors/NumberEditor'
import SelectEditor from './editors/SelectEditor'
import classes from './grid-theme.module.scss'

interface GridCellProps<T> {
  column: GridColumn<T>
  row: T
  rowId: string
  rowIndex: number
  width: number
  api: GridApi<T>
  isPinnedBottom: boolean
  isEditing: boolean
  isChanged: boolean
  isFocused: boolean
  depth: number
  pinned?: 'left' | 'right'
  offset: number
  isPinnedEdge: boolean
  onCellClick: (event: React.MouseEvent, rowId: string, field: string, editable: boolean) => void
  onCommit: (
    rowId: string,
    column: GridColumn<T>,
    row: T,
    value: unknown,
    /** The value the editor was opened on — see DataGrid's stale-editor guard. */
    baseline: unknown,
  ) => void
  /** Draws the drag-to-fill square in this cell's bottom-right corner. */
  showFillHandle?: boolean
  /** Covered by the fill drag in progress. */
  inFillPreview?: boolean
  fillPreviewEdge?: 'single' | 'top' | 'bottom' | 'middle' | null
  /** Passed to the editor: open its list on mount. */
  editorOpenList?: boolean
  onFillHandlePointerDown?: (event: React.PointerEvent) => void
  /** Fills this column from here to the last row — dragging 983 rows is not a plan. */
  onFillHandleDoubleClick?: (event: React.MouseEvent) => void
  onCancelEdit: () => void
  onKeyDown?: (event: React.KeyboardEvent, rowId: string, field: string, row: T) => void
}

const resolveEditor = <T,>(column: GridColumn<T>): FC<GridEditorProps<T>> | null => {
  if (!column.editor) return null
  if (column.editor === 'text') return TextEditor as unknown as FC<GridEditorProps<T>>
  if (column.editor === 'number') return NumberEditor as unknown as FC<GridEditorProps<T>>
  if (column.editor === 'select') return SelectEditor as unknown as FC<GridEditorProps<T>>
  return column.editor
}

const GridCellInner = <T,>({
  column,
  row,
  rowId,
  rowIndex,
  width,
  api,
  isPinnedBottom,
  isEditing,
  isChanged,
  isFocused,
  depth,
  pinned,
  offset,
  isPinnedEdge,
  onCellClick,
  onCommit,
  onCancelEdit,
  onKeyDown,
  showFillHandle = false,
  inFillPreview = false,
  fillPreviewEdge = null,
  editorOpenList = false,
  onFillHandlePointerDown,
  onFillHandleDoubleClick,
}: GridCellProps<T>) => {
  const value = column.valueGetter
    ? column.valueGetter(row)
    : (row as Record<string, unknown>)[column.field]

  const ctx = useMemo<GridCellCtx<T>>(
    () => ({ row, rowId, value, rowIndex, isPinnedBottom, api, depth }),
    [row, rowId, value, rowIndex, isPinnedBottom, api, depth],
  )

  const editable =
    typeof column.editable === 'function' ? column.editable(ctx) : Boolean(column.editable)

  const Editor = resolveEditor(column)

  const extraClass = useMemo(() => {
    if (!column.cellClass) return ''
    const resolved =
      typeof column.cellClass === 'function' ? column.cellClass(ctx) : column.cellClass
    return Array.isArray(resolved) ? resolved.filter(Boolean).join(' ') : resolved
  }, [column, ctx])

  const extraStyle = useMemo(
    () =>
      typeof column.cellStyle === 'function' ? column.cellStyle(ctx) : column.cellStyle,
    [column, ctx],
  )

  const pinnedClass =
    pinned === 'left'
      ? `${classes.cellPinnedLeft} ${isPinnedEdge ? classes.pinnedLeftEdge : ''}`
      : pinned === 'right'
        ? `${classes.cellPinnedRight} ${isPinnedEdge ? classes.pinnedRightEdge : ''}`
        : ''

  return (
    <div
      className={[
        classes.cell,
        column.numeric ? classes.cellNumeric : '',
        column.wrapText ? classes.cellWrap : '',
        isEditing ? classes.cellEditing : '',
        isChanged ? classes.cellChanged : '',
        isFocused && !isEditing ? classes.cellFocused : '',
        pinnedClass,
        inFillPreview ? classes.cellFillPreview : '',
        inFillPreview && fillPreviewEdge
          ? classes[
              `cellFillPreview${
                fillPreviewEdge.charAt(0).toUpperCase() + fillPreviewEdge.slice(1)
              }` as keyof typeof classes
            ]
          : '',
        extraClass,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        width,
        minWidth: width,
        maxWidth: width,
        ...(pinned === 'left' ? { left: offset } : {}),
        ...(pinned === 'right' ? { right: offset } : {}),
        ...extraStyle,
      }}
      data-col-id={column.field}
      role="gridcell"
      onClick={(event) => onCellClick(event, rowId, column.field, editable && !!Editor)}
      onKeyDown={(event) => onKeyDown?.(event, rowId, column.field, row)}
    >
      {showFillHandle && (
        // Sits over the cell's corner rather than in its flow, so it cannot
        // push a value around or steal a click meant for the text.
        <div
          className={classes.fillHandle}
          data-no-cell-edit
          role="presentation"
          title="Drag to fill down — double-click to fill to the end"
          onPointerDown={onFillHandlePointerDown}
          onDoubleClick={onFillHandleDoubleClick}
        />
      )}
      {isEditing && Editor ? (
        <Editor
          value={value}
          openOnMount={editorOpenList}
          row={row}
          rowId={rowId}
          api={api}
          params={
            typeof column.editorParams === 'function'
              ? column.editorParams(ctx)
              : column.editorParams
          }
          onCommit={(next) => onCommit(rowId, column, row, next, value)}
          onCancel={onCancelEdit}
        />
      ) : column.cellRenderer ? (
        <column.cellRenderer {...ctx} />
      ) : (
        (value as React.ReactNode) ?? null
      )}
    </div>
  )
}

// Rows re-render on every pending-edit change; without this the whole grid
// re-renders each keystroke.
const GridCell = memo(GridCellInner) as typeof GridCellInner

export default GridCell
